import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import vaultsData from '../../data/vaults.json';
import tradesData from '../../data/trades.json';

const VaultDetailView = () => {
  const navigate = useNavigate();
  const { botId } = useParams();
  
  // Find vault data from our JSON
  const vault = vaultsData.vaults.find(v => v.id === botId) || vaultsData.vaults[0];
  const vaultTrades = tradesData.trades[botId] || [];

  const [selectedTimeframe, setSelectedTimeframe] = useState('7d');

  const {
    id = '',
    name = 'Trading Bot',
    exchange = 'Unknown',
    status = 'active',
    statusReason = '',
    winRate = 0,
    sharpeRatio = 0,
    maxDrawdown = 0,
    totalTrades = 0,
    totalProfit = 0,
    profitPercent = 0,
    performance24h = 0,
    performance7d = 0,
    performance30d = 0,
    activePositions = 0,
    currentValue = 0,
    initialCapital = 0,
    description = '',
    strategy = '',
    riskLevel = 'Medium',
    tradeSize = { min: 0, max: 0 },
    targetProfitRange = { min: 0, max: 0 },
    targetCoins = [],
    activeSince = ''
  } = vault || {};

  // Strategy descriptions based on vault type
  const strategyDescriptions = {
    'mexc-shorting-bot': {
      title: 'Pump Detection & Shorting Strategy',
      description: description || 'Shorts low-cap coins on MEXC when they experience sudden price bursts without supporting fundamentals.',
      riskLevel: riskLevel,
      targetMarkets: ['MEXC'],
      approach: 'Momentum Short Selling',
      keyIndicators: ['Volume Spike Detection', 'Social Sentiment Analysis', 'Order Book Imbalance', 'Price Momentum']
    },
    'binance-delisting-bot': {
      title: 'Delisting Event Arbitrage',
      description: description || 'Capitalizes on Binance delisting announcements by shorting tokens before removal.',
      riskLevel: riskLevel,
      targetMarkets: ['Binance'],
      approach: 'Event-Driven Short Selling',
      keyIndicators: ['Delisting Announcements', 'Liquidity Analysis', 'Historical Delisting Patterns', 'Exchange API Monitoring']
    },
    'prediction-futures-bot': {
      title: 'Predictive Model Trading',
      description: description || 'Uses machine learning models to identify high-probability futures trading opportunities.',
      riskLevel: riskLevel,
      targetMarkets: ['Prediction Futures'],
      approach: 'ML-Driven Futures Trading',
      keyIndicators: ['Price Predictions', 'Market Regime Detection', 'Volatility Forecasting', 'Correlation Analysis']
    },
    'market-implied-lighter-bot': {
      title: 'Market-Implied Signal Validation',
      description: description || 'Validates market-implied directional signals in paper mode before full production deployment.',
      riskLevel: riskLevel,
      targetMarkets: ['Lighter'],
      approach: 'Implied Metrics Directional Testing',
      keyIndicators: ['Basis Dislocation', 'Funding Skew', 'Perp/Spot Divergence', 'Signal Confidence Filter']
    },
    'euphoria-bot': {
      title: 'Odds-Implied Directional Validation',
      description: description || 'Validates directional signals derived from Euphoria Finance prediction-market odds and trade-tape flow, cross-referenced against spot reference prices, in paper mode before any live deployment.',
      riskLevel: riskLevel,
      targetMarkets: ['Euphoria Finance'],
      approach: 'Odds-Implied Signal Testing',
      keyIndicators: ['Odds Surface Skew', 'Trade-Tape Imbalance', 'Reference-Price Lead/Lag', 'Signal Confidence Filter']
    }
  };

  const strategyInfo = strategyDescriptions[botId] || strategyDescriptions['mexc-shorting-bot'];

  const getStatusColor = () => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success';
      case 'paused':
        return 'bg-warning/10 text-warning';
      case 'halted':
        return 'bg-error/10 text-error';
      case 'testing':
        return 'bg-accent/10 text-accent';
      case 'error':
        return 'bg-error/10 text-error';
      case 'archived':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'active':
        return 'Play';
      case 'paused':
        return 'Pause';
      case 'halted':
        return 'AlertOctagon';
      case 'testing':
        return 'FlaskConical';
      case 'error':
        return 'AlertCircle';
      case 'archived':
        return 'Archive';
      default:
        return 'Circle';
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'paused':
        return 'Paused';
      case 'halted':
        return 'Halted';
      case 'testing':
        return 'Testing';
      case 'archived':
        return 'Archived';
      default:
        return status?.charAt(0)?.toUpperCase() + status?.slice(1);
    }
  };

  const getProfitLossColor = (value) => {
    if (value > 0) return 'text-success';
    if (value < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })?.format(value);
  };

  const formatPercent = (value) => {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value?.toFixed(2)}%`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(date);
  };

  const getRiskColor = (level) => {
    if (level?.includes('Low')) return 'text-success';
    if (level?.includes('High')) return 'text-error';
    return 'text-warning';
  };

  const handleClose = () => {
    navigate('/main-dashboard');
  };

  // Get recent trades sorted by timestamp
  const recentTrades = [...vaultTrades]
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, 10);

  return (
    <>
      <Helmet>
        <title>{name} - Vault Detail - TheVault</title>
        <meta name="description" content={`Detailed performance analytics and strategy information for ${name}`} />
      </Helmet>
      <div className="min-h-screen bg-background">
        <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 overflow-y-auto">
          <div className="min-h-screen py-8 px-4 md:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleClose}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-smooth"
                    aria-label="Close"
                  >
                    <Icon name="X" size={20} />
                  </button>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground">
                      {name}
                    </h1>
                    <p className="text-sm text-muted-foreground caption">{exchange}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1.5 rounded-lg text-sm font-medium caption ${getStatusColor()}`}>
                    <Icon name={getStatusIcon()} size={14} className="inline mr-1" />
                    {getStatusLabel()}
                  </span>
                </div>
              </div>

              {(status === 'halted' || status === 'testing' || status === 'paused' || status === 'archived') && statusReason && (
                <div className={`mb-6 p-4 rounded-lg border ${
                  status === 'testing' ? 'bg-accent/10 border-accent/20'
                    : status === 'paused' ? 'bg-warning/10 border-warning/20'
                    : status === 'archived' ? 'bg-muted border-border'
                    : 'bg-error/10 border-error/20'
                }`}>
                  <div className="flex items-start gap-3">
                    <Icon
                      name={
                        status === 'testing' ? 'FlaskConical'
                          : status === 'paused' ? 'Pause'
                          : status === 'archived' ? 'Archive'
                          : 'AlertTriangle'
                      }
                      size={20}
                      className={`mt-0.5 ${
                        status === 'testing' ? 'text-accent'
                          : status === 'paused' ? 'text-warning'
                          : status === 'archived' ? 'text-muted-foreground'
                          : 'text-error'
                      }`}
                    />
                    <div>
                      <p className={`text-sm font-medium ${
                        status === 'testing' ? 'text-accent'
                          : status === 'paused' ? 'text-warning'
                          : status === 'archived' ? 'text-muted-foreground'
                          : 'text-error'
                      }`}>
                        {status === 'testing' ? 'Vault In Testing'
                          : status === 'paused' ? 'Vault Paused'
                          : status === 'archived' ? 'Vault Archived'
                          : 'Vault Halted'}
                      </p>
                      <p className={`text-sm ${
                        status === 'testing' ? 'text-accent/80'
                          : status === 'paused' ? 'text-warning/80'
                          : status === 'archived' ? 'text-muted-foreground'
                          : 'text-error/80'
                      }`}>{statusReason}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Vault Value Summary */}
              <div className="mb-6 p-6 rounded-[10px] bg-card border border-border shadow-elevation-1">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-xs text-muted-foreground caption mb-1">Current Value</p>
                    <p className="text-3xl font-heading font-bold text-foreground data-text">
                      {formatCurrency(currentValue)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground caption mb-1">Initial Capital</p>
                    <p className="text-2xl font-heading font-semibold text-muted-foreground data-text">
                      {formatCurrency(initialCapital)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground caption mb-1">Total Profit</p>
                    <p className={`text-2xl font-heading font-semibold data-text ${getProfitLossColor(totalProfit)}`}>
                      {formatCurrency(totalProfit)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground caption mb-1">ROI</p>
                    <p className={`text-2xl font-heading font-semibold data-text ${getProfitLossColor(profitPercent)}`}>
                      {formatPercent(profitPercent)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-card rounded-[10px] p-6 border border-border shadow-elevation-1">
                    <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                      Strategy Overview
                    </h2>
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-heading font-medium text-foreground mb-2">
                          {strategyInfo?.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {strategyInfo?.description}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                        <div>
                          <p className="text-xs text-muted-foreground caption mb-1">Risk Level</p>
                          <p className={`text-sm font-medium ${getRiskColor(strategyInfo?.riskLevel)}`}>
                            {strategyInfo?.riskLevel}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground caption mb-1">Approach</p>
                          <p className="text-sm font-medium text-foreground">
                            {strategyInfo?.approach}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                        <div>
                          <p className="text-xs text-muted-foreground caption mb-1">Trade Size Range</p>
                          <p className="text-sm font-medium text-foreground">
                            {formatCurrency(tradeSize.min)} - {formatCurrency(tradeSize.max)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground caption mb-1">Target Profit Range</p>
                          <p className="text-sm font-medium text-success">
                            {targetProfitRange.min}% - {targetProfitRange.max}%
                          </p>
                        </div>
                      </div>

                      {targetCoins.length > 0 && (
                        <div className="pt-4 border-t border-border">
                          <p className="text-xs text-muted-foreground caption mb-2">Target Assets</p>
                          <div className="flex flex-wrap gap-2">
                            {targetCoins?.map((coin, index) => (
                              <span
                                key={index}
                                className="px-3 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium"
                              >
                                {coin}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="pt-4 border-t border-border">
                        <p className="text-xs text-muted-foreground caption mb-2">Key Indicators</p>
                        <div className="flex flex-wrap gap-2">
                          {strategyInfo?.keyIndicators?.map((indicator, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 rounded-md bg-muted text-foreground text-xs font-medium"
                            >
                              {indicator}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card rounded-[10px] p-6 border border-border shadow-elevation-1">
                    <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                      Performance Analytics
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground caption">Win Rate</p>
                        <p className="text-2xl font-heading font-semibold text-foreground data-text">
                          {winRate?.toFixed(1)}%
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground caption">Sharpe Ratio</p>
                        <p className="text-2xl font-heading font-semibold text-foreground data-text">
                          {sharpeRatio?.toFixed(2)}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground caption">Max Drawdown</p>
                        <p className="text-2xl font-heading font-semibold text-error data-text">
                          {maxDrawdown?.toFixed(1)}%
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground caption">Total Trades</p>
                        <p className="text-2xl font-heading font-semibold text-foreground data-text">
                          {totalTrades?.toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-border">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-heading font-medium text-foreground">
                          Performance Trends
                        </h3>
                        <div className="flex gap-2">
                          {['24h', '7d', '30d']?.map((tf) => (
                            <button
                              key={tf}
                              onClick={() => setSelectedTimeframe(tf)}
                              className={`px-3 py-1 rounded-md text-xs font-medium transition-smooth ${
                                selectedTimeframe === tf
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
                              }`}
                            >
                              {tf}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground caption">24h</p>
                          <p className={`text-lg font-medium data-text ${getProfitLossColor(performance24h)}`}>
                            {formatPercent(performance24h)}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground caption">7d</p>
                          <p className={`text-lg font-medium data-text ${getProfitLossColor(performance7d)}`}>
                            {formatPercent(performance7d)}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground caption">30d</p>
                          <p className={`text-lg font-medium data-text ${getProfitLossColor(performance30d)}`}>
                            {formatPercent(performance30d)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-border">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground caption">Active Since</p>
                          <p className="text-lg font-heading font-semibold text-foreground">
                            {new Date(activeSince).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground caption">Active Positions</p>
                          <p className="text-lg font-heading font-semibold text-foreground data-text">
                            {activePositions}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-1">
                  <div className="bg-card rounded-[10px] p-6 border border-border shadow-elevation-1 sticky top-8">
                    <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                      Recent Trades
                    </h2>
                    <div className="space-y-3 max-h-[600px] overflow-y-auto custom-scrollbar">
                      {recentTrades.length > 0 ? (
                        recentTrades?.map((trade) => (
                          <div
                            key={trade?.id}
                            className="p-4 rounded-lg bg-muted/50 border border-border hover:bg-muted transition-smooth"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    trade?.type === 'short' ? 'bg-error/10 text-error' : 'bg-success/10 text-success'
                                  }`}
                                >
                                  {trade?.type?.toUpperCase()}
                                </span>
                                <span className="text-sm font-medium text-foreground">
                                  {trade?.coin}
                                </span>
                                {trade?.status === 'open' && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary">
                                    OPEN
                                  </span>
                                )}
                              </div>
                              <p className={`text-sm font-medium data-text ${getProfitLossColor(trade?.profitLoss)}`}>
                                {trade?.status === 'open' ? '~' : ''}{formatCurrency(trade?.profitLoss)}
                              </p>
                            </div>
                            <div className="space-y-1 text-xs text-muted-foreground caption">
                              <div className="flex justify-between">
                                <span>Size:</span>
                                <span className="data-text">{formatCurrency(trade?.size)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Duration:</span>
                                <span>{trade?.duration}</span>
                              </div>
                              <div className="flex justify-between pt-1 border-t border-border">
                                <span>P/L:</span>
                                <span className={`font-medium ${getProfitLossColor(trade?.profitLossPercent)}`}>
                                  {formatPercent(trade?.profitLossPercent)}
                                </span>
                              </div>
                            </div>
                            <p className="text-xs text-muted-foreground caption mt-2">
                              {formatDate(trade?.timestamp)}
                            </p>
                            {trade?.note && (
                              <p className="text-xs text-muted-foreground/70 mt-1 italic">{trade?.note}</p>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Icon name="FileX" size={32} className="mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No trades yet</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground caption">Total Trades</p>
                        <p className="text-lg font-heading font-semibold text-foreground data-text">
                          {totalTrades}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VaultDetailView;
